import { useMemo } from 'react'

export interface GroupedPropertyRate {
  /**
   * Unique key used for grouping rates together (boardCode::inclusionKey).
   * This is the key used to ensure uniqueness when grouping and should be used as React key.
   */
  groupingKey: string

  /**
   * All rates within the group (non-refundable, partially refundable, and fully refundable).
   * Preserves original order from accommodation service.
   */
  rates: Array<App.PropertyRate>
}

/**
 * Extract inclusion-related parts from groupKey by removing board code and cancellation policy
 * This uses accommodation service's normalization logic which coalesces meal-related inclusions together.
 */
function extractInclusionKeyFromGroupKey(groupKey: string): string {
  const parts = groupKey.split('-')
  // Skip first two parts (board code and cancellation policy), keep the rest
  return parts.slice(2).join('-')
}

/**
 * Groups Property rates by package type (boardCode) and inclusions.
 *
 * Strategy:
 * 1. Group rates by package type (boardCode) and inclusions (extracted from groupKey)
 * 2. Preserve all rates within each group (non-refundable, partially refundable, and fully refundable)
 * 3. Preserve original order from accommodation service (which sorts by LuxPlus+ price where available)
 *
 * Uses accommodation service's groupKey for inclusion matching, which has more aggressive
 * normalization logic and coalesces meal-related inclusions together.
 *
 * @param rates - Array of property rates (with defaultOrder field from accommodation service)
 * @param enabled - Whether the merged package options feature is enabled (from AB test)
 * @returns Grouped rates by package type and inclusions
 */

function useGroupedPropertyRatesByRefundability(
  rates: Array<App.PropertyRate>,
  enabled: boolean,
): Array<GroupedPropertyRate> {
  return useMemo(() => {
    if (!enabled) {
      return [] // Feature disabled, use original logic
    }

    const grouped = new Map<string, GroupedPropertyRate>()

    rates.forEach(rate => {
      const boardCode = rate.boardCode
      // Use accommodation service's groupKey for inclusion matching, which has better normalization
      const inclusionKey = extractInclusionKeyFromGroupKey(rate.groupKey)

      // Group by boardCode and inclusionKey only - SPP treats LPC and bedbank rates the same
      const groupingKey = `${boardCode}::${inclusionKey}`

      const existing = grouped.get(groupingKey) || {
        groupingKey,
        rates: [] as Array<App.PropertyRate>,
      }

      existing.rates.push(rate)

      grouped.set(groupingKey, existing)
    })

    return Array.from(grouped.values())
  }, [rates, enabled])
}

export default useGroupedPropertyRatesByRefundability
